import { Component, HostListener, input, model, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Asset } from 'app/classes/asset';
import { IDataClassConfig } from 'app/classes/data-table/data-table';
import { IIssueAttachment, IIssueEntity, Issue, IssueVisibility } from 'app/classes/issue';
import { Telemetry } from 'app/classes/telemetry';
import { DataForm } from 'app/layout/data-form/data-form.component';
import { APIService } from 'app/shared/api.service';
import { AssetService, IGetAnnotationItem, IGetTelemetryTelemetry } from 'app/shared/asset.service';
import { BuildingsService } from 'app/shared/buildings.service';
import { DateService } from 'app/shared/date.service';
import { IssueTrackingStatusType, IssueTrackingService, ITrackingIssue, IssueTrackingProgressType } from 'app/shared/issue-tracking.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { User } from 'app/classes/user';
import { IDatesFromTo } from 'app/interfaces/dates-from-to';
import { WindowService } from 'app/shared/window.service';

@Component({
  selector: 'app-issue-tracking-item',
  standalone: false,
  templateUrl: './issue-tracking-item.component.html',
  styleUrl: './issue-tracking-item.component.css'
})
export class IssueTrackingItemComponent implements OnInit {
  issue = signal<Issue>(null);
  telemetry = signal<Telemetry[]>(null);
  dataForm: DataForm;
  asset: Asset;
  assets = signal<{ asset: Asset, telemetry: IGetTelemetryTelemetry[], annotations: IGetAnnotationItem[] }[]>(null);
  annotations = signal<any[]>(null);
  hasPageTitle = false;
  dialogOpen = signal<'status' | 'edit' | 'entities' | 'verify_entity'>(null);
  wantsStatus = signal<IssueTrackingStatusType>(null);
  documentLink: SafeResourceUrl;
  proposedAction: string;
  editForm = signal<{ substatus_id: number; description: string, title: string, rag: string, taskProgress: IssueTrackingProgressType, visibility: IssueVisibility, assignee: number, status: IssueTrackingStatusType, completedAt: string, resolvedAt: string }>(null);
  isDragging = false;
  hasDropped = false;

  chartRange = signal<IDatesFromTo>(null);
  orgUsers: User[];

  newEntity: IIssueEntity;

  can: { edit: boolean, changeRAG: boolean } = { changeRAG: false, edit: false };

  dialogConfirm = signal<'delete'>(null);

  user: User;

  statusOptions: { id: number, title: string }[];
  isMobile = false;

  isUpdating = signal<boolean>(false);

  @HostListener("dragenter", ["$event"])
  onDragStartHandler(event: DragEvent) {

    if (this.hasDropped) {
      return;
    }
    this.isDragging = true;
    event.dataTransfer.dropEffect = "copy";
    event.preventDefault();
    console.log("dragstart", event);
  }

  @HostListener("dragover", ["$event"])
  onDragOverHandler(event: DragEvent) {

    if (this.hasDropped) {
      return;
    }
    this.isDragging = true;
    event.dataTransfer.dropEffect = "copy";
    event.preventDefault();
  }

  @HostListener("dragleave", ["$event"])
  onDragLeaveHandler(event: any) {
    if (event.toElement.localName === "td") {
      return;
    }

    event.preventDefault();
    console.info("dragleave", event.toElement.localName, event);
  }

  @HostListener("drop", ["$event"])
  onDropHandler(event: DragEvent) {

    event.preventDefault();
    event.stopPropagation();
    console.log("drop", event);

    const file = event.dataTransfer.files[0];
    const { name, type } = file;

    if (!name || name.toLowerCase().substr(name.length - 3) === "exe") {
      return this.apiService.toastWarn("Not allowed", `received ${name}`);
    }

    const title = "File attachment ";
    const id = `${this.issue().id}`;
    const filename = name.toLowerCase();

    this.apiService.toastSuccess("uploading", "please wait...");

    this.apiService.postUploader(title, false, 'issue_tracking', id, type, filename, file).then((r) => {
      console.log(r);

      this.hasDropped = false;
      this.isDragging = false;
      setTimeout(() => {
        this.getIssue(this.issue().id);
        this.apiService.toastSuccess("uploaded", "", 2000);
      }, 1000);
    });

  }

  constructor(private sanitizer: DomSanitizer, private buildingReviewService: BuildingsService, private issueTrackingService: IssueTrackingService, private assetService: AssetService, private apiService: APIService, private route: ActivatedRoute, private router: Router, private windowService: WindowService) {
    this.issueTrackingService.getAllowedAssignees().then(r => this.orgUsers = r);
    this.can.changeRAG = this.apiService.isAdmin() || this.apiService.hasOrgRole('issuetracking_admin');
    this.user = this.apiService.getThisUser();
    this.statusOptions = issueTrackingService.getOptionsForStatus();
    this.isMobile = this.windowService.isMobile();
  }

  ngOnInit(): void {
    const config: IDataClassConfig = {};
    this.dataForm = new DataForm({ data: this.issue() },);
    const id = +this.route.snapshot.paramMap.get('id');
    if (id) {
      this.getIssue(id);
    } else {
      this.initTelemetry();
    }
  }

  attachmentClick(attachment: IIssueAttachment) {

  }

  complete() {

  }

  goBack() {
    this.router.navigate(['/issuetracking']);
  }

  edit() {
    this.editForm.set({
      description: this.issue().description,
      rag: this.issue().rag,
      taskProgress: this.issue().taskProgress,
      visibility: this.issue().visibility,
      assignee: this.issue().assignee ? +this.issue().assignee : null,
      status: this.issue().status,
      title: this.issue().title,
      substatus_id: this.issue().substatus_id,
      completedAt: this.issue().completedAt ? this.issue().completedAt.toISOString().split('T')[0] : null,
      resolvedAt: this.issue().resolvedAt ? this.issue().resolvedAt.toISOString().split('T')[0] : null,
    });

    this.dialogOpen.set('edit');
  }

  setRag(rag: string) {
    this.editForm.update(issue => {
      issue.rag = rag;
      return issue;
    });
  }

  onFileSelected(ev: any) {

  }

  completeIssueUsingDate(date: Date) {
    this.isUpdating.set(true);
    this.apiService.toastSuccess('updating completed date...', null, 500);

    const payload = {
      completedAt: DateService.dbDate(date)
    };

    this.issueTrackingService.updateIssue(this.issue().id, payload).then(() => {
      this.dialogOpen.set(null);
      this.getIssue(this.issue().id);
      this.isUpdating.set(false);
      setTimeout(() => {
        this.router.navigate(['/issuetracking']);
        this.apiService.toastSuccess('saved.');
      }, 1000);

    });
  }

  issueUpdated(event: any) {
    this.router.navigate(['/issuetracking']);
  }

  issueCompleted(event: any) {
    this.router.navigate(['/issuetracking']);
  }

  saveEdit() {
    const payload: any = {};
    if (this.editForm().title !== this.issue().title) {
      payload.title = this.editForm().title;
    }

    if (this.editForm().visibility !== this.issue().visibility) {
      payload.visibility = this.editForm().visibility;
    }

    if (this.editForm().description !== this.issue().description) {
      payload.description = this.editForm().description;
    }

    if (this.editForm().rag !== this.issue().rag) {
      payload.rag = this.editForm().rag;
    }

    if (this.editForm().taskProgress !== this.issue().taskProgress) {
      payload.taskProgress = this.editForm().taskProgress;
    }

    if (this.editForm().assignee !== this.issue().assignee) {
      payload.assignee = this.editForm().assignee;
    }

    if (this.editForm().status !== this.issue().status) {
      payload.status = this.editForm().status;
    }

    if (this.editForm().substatus_id !== this.issue().substatus_id) {
      payload.substatus_id = this.editForm().substatus_id;
    }

    if (this.editForm().completedAt !== (this.issue().completedAt?.toISOString().split('T')[0] ?? null)) {
      payload.completedAt = DateService.dbDate(this.editForm().completedAt);
    }

    if (this.editForm().resolvedAt !== (this.issue().resolvedAt?.toISOString().split('T')[0] ?? null)) {
      const resolvedAt = this.editForm().resolvedAt ?? null;
      if (resolvedAt !== "" && resolvedAt !== null) {
        payload.resolvedAt = DateService.dbDate(this.editForm().resolvedAt);
      } else {
        payload.resolvedAt = null;
      }
    }

    if (Object.keys(payload).length === 0) {
      this.apiService.toastWarn('nothing changed.');
      this.dialogOpen.set(null);

      return;
    };


    const testSubStatusId = payload.substatus_id ?? this.issue().substatus_id;

    if (payload.resolvedAt && testSubStatusId < 6) {
      this.apiService.toastWarn('Cannot resolve issue if open.', 'Select a resolved status.', 5000);

      return;
    }

    this.isUpdating.set(true);
    this.apiService.toastSuccess('saving...', null, 500);

    this.issueTrackingService.updateIssue(this.issue().id, payload).then(() => {
      this.dialogOpen.set(null);
      this.getIssue(this.issue().id);
      this.isUpdating.set(false);
      setTimeout(() => {
        this.router.navigate(['/issuetracking']);
        this.apiService.toastSuccess('saved.');
      }, 1000);

    });
  }

  linkEntity() {
    this.dialogOpen.set('entities');
  }

  entitiesSelected(response: { type: string, obj: any }) {
    console.log('RESPONSE', response);
    this.newEntity = { id: null, field: response.type + '_id', value: response.obj.id, title: response.obj.title ?? response.obj.name ?? 'No title' };

    const exists = this.issue().entities.find(entity => entity.field === this.newEntity.field && entity.value === this.newEntity.value);

    if (exists) {
      if (this.newEntity.field === 'org_id') {
        // user wants to change the org 
      } else {
        this.apiService.toastWarn('Entity already exists.');
        return;
      }

    }

    this.dialogOpen.set('verify_entity');
  }

  userVerifiedAddingEntity() {
    this.issue.update(issue => {
      issue.addEntity(this.newEntity);

      return issue;
    });
    this.apiService.toastSuccess('Updating entitites...', null, 1000);
    this.issueTrackingService.updateEntitiesForIssue(this.issue().id, { add: [this.newEntity] }).then(() => {
      this.apiService.toastSuccess('Saved changes.', null, 1000);
      this.dialogOpen.set(null);
      this.reload();
    });
  }

  reload() {
    this.getIssue(this.issue().id);
  }

  getIssue(id: number) {
    this.hasPageTitle = true;
    this.issueTrackingService.getIssue(id).then((issue) => {
      this.issue.set(issue);
      const range = issue?.points?.[0] ?? {
        fromDate: DateService.getDateStartOfMonth(DateService.subMonths(new Date(), 2)), toDate: new Date()
      };

      this.setChartRange(range?.fromDate, range?.toDate);
      this.documentLink = issue.buildingReview?.documentKey ? this.sanitizer.bypassSecurityTrustResourceUrl('https://s3.eu-west-2.amazonaws.com/4d-documents/' + issue.buildingReview.documentKey) : null;
      this.initTelemetry();
      if (this.apiService.getUserId() === issue.assignee) {
        this.can.edit = true;
      }
    });
  }

  setChartRange(from, to) {
    console.log('SET CHART RANGE', from, to);

    this.chartRange.set({ from, to });
  }

  initTelemetry() {
    this.getTelemetryForAllAssets();
  }

  getTelemetry() {
    const assetId = this.issue().assets[0]?.id;
    if (!assetId) {
      return;
    }
    this.assetService.getAsset(+assetId).then((asset: any) => {
      this.asset = asset;

      const from = this.chartRange()?.from ?? DateService.subMonths(new Date(), 2);
      const to = this.chartRange()?.to ?? new Date();

      this.assetService.getTelemetry(+assetId, from, to).then((response: any) => {
        const telemetry: Telemetry[] = response.telemetry;
        this.telemetry.set(telemetry);
      });
    });
  }

  getTelemetryForAllAssets() {
    this.assets.set(null);


    if (!this.chartRange()?.from || !this.chartRange()?.to) {
      const from = this.chartRange()?.from ?? DateService.subMonths(new Date(), 2);
      const to = this.chartRange()?.to ?? new Date();

      this.setChartRange(from, to);
    }

    const { from, to } = this.chartRange();

    for (let i = 0; i < this.issue().assets.length; i++) {
      const assetId = +(this.issue().assets[i]?.id);
      this.assetService.getAsset(+assetId).then(asset => {
        this.assetService.getTelemetry(assetId, from, to).then((response) => {
          this.assetService.getAnnotations(assetId).then((annotations) => {
            const assetContainer = { asset, telemetry: response.telemetry, annotations };
            this.assets.update(assets => {
              if (assets === null) {
                assets = [];
              }
              assets.push(assetContainer);
              return assets;
            });
          });
        });
      });
    }
  }

  ChangeStatus() {
    this.dialogOpen.set('status');
  }

  setStatus(status: IssueTrackingStatusType) {
    this.wantsStatus.set(status);
  }

  async saveStatus() {
    this.apiService.toastSuccess('setting status...', null, 500);
    await this.issueTrackingService.setStatus(this.issue().id, this.wantsStatus(), this.proposedAction);
    this.apiService.toastSuccess('status set.');
    this.router.navigate(['/issuetracking']);
  }
}
